/*
 * Copyright (c) 2024 Cozy Intellect Inc.
 * ChatService singleton for sharedlib
 * This creates and manages the ChatService instance with proper context initialization
 */

import type { ChatLibContext, ChatService } from '@repo/common/types/Chat';
import type { SharedLibHandler } from '@repo/common/types/SharedLib';
import type { UserAuth } from '@repo/common/types/User';
import { derived, get } from 'svelte/store';
import { loadSignedInUser, signOut } from './actions/auth.actions';
import { getChatLibContext, setChatLibContext } from './chat.service.context';
import {
  chatServiceHistory,
  chatServiceIsLoading,
  chatServiceSections
} from './chat.service.store';
import { debouncedUpsertItemDataToCloud } from './data/api.memory';
import { getPrivateClient } from './data/common.data';
import { getHelpArticle, getHelpArticles } from './data/help-articles.data';
import { raptorSearch } from './data/raptor.data';
import { getHandlerAsync } from './sharedlib';
import { agentRx, agentTx } from './stores/agent-draft.store';
import { isSignedIn, user, userAuthStore, userAuthToken } from './stores/auth.store';
import { getCalendarEventByTranscript } from './stores/calendar.store';
import { chatInfoStore } from './stores/composites';
import { getApiEndpoint, getConfig } from './stores/config';
import { ensureConversation, ensureConversationByTranscript } from './stores/conversation.store';
import { createFilteredThread } from './stores/filtered-type';
import { FilteredMemory } from './stores/filtered-type/filtered-memory';
import { FilteredThread } from './stores/filtered-type/filtered-thread';
import { addGoals } from './stores/goals';
import { messages } from './stores/messages';
import { taskStore } from './stores/task.store';
import { threadsStore } from './stores/threads.store';
import {
  ensureTranscriptFull,
  getTranscript,
  getFullTranscript as getFullTranscriptFromDB,
  transcriptMap
} from './stores/transcripts';
import { uiCalendarEvents } from './stores/ui-calendar-events/core.store';
import { workspaceStore } from './stores/workspace.store';
import { DEFAULT_MODEL } from './utils/aiModels';
import constants from './utils/constants';

// Create the service instance with the context getter
let chatServiceInstance: ChatService | null = null;
let isInitialized = false;

/**
 * Builds the ChatLibContext from sharedlib's own stores and methods
 * This ensures the context is always up-to-date with the current state
 * @param handler - The SharedLibHandler instance (optional for initial context build)
 */
function buildChatLibContext(handler?: SharedLibHandler): ChatLibContext {
  if (!user) {
    throw new Error('User store not found');
  }
  if (!userAuthToken) {
    throw new Error('User auth token store not found');
  }
  if (!userAuthStore) {
    throw new Error('User auth store not found');
  }
  if (!isSignedIn) {
    throw new Error('Is signed in store not found');
  }
  if (!taskStore) {
    throw new Error('Task store not found');
  }
  if (!workspaceStore) {
    throw new Error('Workspace store not found');
  }
  if (!transcriptMap) {
    throw new Error('Transcript map store not found');
  }
  if (!chatInfoStore) {
    throw new Error('Chat info store not found');
  }
  if (!messages) {
    throw new Error('Messages store not found');
  }
  if (!uiCalendarEvents) {
    throw new Error('UI calendar events store not found');
  }
  if (!threadsStore) {
    throw new Error('Threads store not found');
  }
  if (!agentRx) {
    throw new Error('Agent Rx store not found');
  }
  if (!agentTx) {
    throw new Error('Agent Tx store not found');
  }
  if (!chatServiceHistory) {
    throw new Error('Chat service history store not found');
  }
  return {
    handler: handler || ({} as SharedLibHandler),
    stores: {
      user: derived(user, (user) => user),
      userAuthToken,
      userAuthStore,
      isSignedIn,
      taskStore: taskStore as any, // taskStore has a complex interface
      workspaceStore,
      transcriptMap,
      chatInfoStore,
      messages: messages as any, // messages is Writable<Messages> but interface expects Writable<Message[]>
      uiCalendarEvents: uiCalendarEvents as any, // uiCalendarEvents has a complex interface
      threadsStore: threadsStore as any, // threadsStore has a complex interface
      agentRx,
      agentTx,
      chatServiceSections,
      chatServiceHistory,
      chatServiceIsLoading
    },
    filteredTypes: {
      FilteredMemory,
      FilteredThread
    },
    methods: {
      getConfig: async () => {
        return Promise.resolve(getConfig());
      },
      getApiEndpoint,
      getTranscript: (id: string) => {
        try {
          return getTranscript(id);
        } catch (error) {
          if (error instanceof Error && error.message.includes('requires a TranscriptFull')) {
            // Warm the cache in the background so the next render can use the full transcript.
            void getFullTranscriptFromDB(id).catch((loadError) => {
              console.warn('[ChatService] Failed to load full transcript:', id, loadError);
            });
            return undefined;
          }
          console.warn('[ChatService] getTranscript failed:', id, error);
          return undefined;
        }
      },
      getFullTranscript: async (id: string) => {
        try {
          return await getFullTranscriptFromDB(id);
        } catch (error) {
          console.warn('[ChatService] Failed to load full transcript:', id, error);
          return undefined;
        }
      },
      ensureTranscriptFull,
      ensureConversation: async (conversationId: string) => {
        console.log('[ChatService] Context ensureConversation called:', conversationId);
        let conversation = await ensureConversation(conversationId);
        if (!conversation) {
          console.warn(
            '[ChatService] Context ensureConversation failed, retrying once...',
            conversationId
          );
          await new Promise((resolve) => setTimeout(resolve, 500));
          conversation = await ensureConversation(conversationId);
        }
        if (!conversation) {
          console.error(
            '[ChatService] Context ensureConversation failed after retry',
            conversationId
          );
        }
        return conversation;
      },
      ensureConversationByTranscript: async (transcriptId: string) => {
        console.log('[ChatService] Context ensureConversationByTranscript called:', transcriptId);
        const transcript = getTranscript(transcriptId);
        if (!transcript) {
          console.error(
            '[ChatService] Failed to ensure conversation by transcript: Transcript not found',
            transcriptId
          );
          return null;
        }
        // Log userId from handler before ensuring conversation (if handler is available)
        const handlerUserId = handler?.getUserId?.();
        console.log('[ChatService] Ensuring conversation by transcript:', {
          transcriptId,
          handlerUserId
        });

        let conversation = await ensureConversationByTranscript(transcript);

        // Retry once if failed
        if (!conversation) {
          console.warn(
            '[ChatService] Failed to ensure conversation by transcript, retrying once...'
          );
          // Simple delay for retry, adjust as needed
          await new Promise((resolve) => setTimeout(resolve, 500));
          conversation = await ensureConversationByTranscript(transcript);
        }

        if (!conversation) {
          console.error(
            '[ChatService] CRITICAL: Failed to ensure conversation by transcript after retry',
            transcriptId
          );
          return null;
        }

        return conversation;
      },
      getCalendarEventByTranscript: async (transcriptId: string) => {
        const transcript = getTranscript(transcriptId);
        if (!transcript) {
          return undefined;
        }
        return Promise.resolve(getCalendarEventByTranscript(transcript));
      },
      addGoals: async (goals: any[]) => {
        addGoals(goals);
        return Promise.resolve();
      },
      debouncedUpsertItemDataToCloud,
      raptorSearch,
      getHelpArticles: async () => {
        const auth = get(userAuthToken);
        if (!auth?.idToken || !auth?.refreshToken) {
          return [];
        }
        return getHelpArticles(auth);
      },
      getHelpArticle: async (id: string) => {
        const auth = get(userAuthToken);
        if (!auth?.idToken || !auth?.refreshToken) {
          return null;
        }
        return getHelpArticle(id, auth);
      },
      getPrivateClient: (tokens: Pick<UserAuth, 'idToken' | 'refreshToken'>) => {
        // Ensure tokens are not null
        if (!tokens.idToken || !tokens.refreshToken) {
          throw new Error('Auth tokens are required');
        }
        return getPrivateClient(tokens as { idToken: string; refreshToken: string }, undefined);
      },
      signOut,
      loadSignedInUser
    },
    classes: {
      FilteredMemory,
      createFilteredThread
    },
    constants: {
      DEFAULT_MODEL,
      constants
    }
  };
}

/**
 * Initializes the ChatService singleton
 * Must be called by the app after the handler is set
 * @param handler - The SharedLibHandler instance (required)
 */
export async function initializeChatService(handler: SharedLibHandler): Promise<ChatService> {
  const ChatLibModule = await import('@repo/chatlib');
  const { getAgentToolsRegistry } = ChatLibModule;
  const ChatServiceImpl = ChatLibModule.ChatServiceImpl;

  if (isInitialized && chatServiceInstance) {
    return chatServiceInstance;
  }

  try {
    // 1. Build context with stores (handler optional initially)
    const context = buildChatLibContext();

    // 2. Store context locally so getChatLibContext() works
    setChatLibContext(context);

    // 3. NOW create ChatServiceImpl - constructor can access stores from context
    chatServiceInstance = new ChatServiceImpl(getChatLibContext) as ChatService;
    if (!chatServiceInstance) {
      throw new Error('ChatService instance not created');
    }

    // 4. Update handler in context and service instance
    context.handler = handler;
    chatServiceInstance.setHandler(handler);

    // 5. Set context getter on the agent tools registry
    const registry = getAgentToolsRegistry();
    registry.setContextGetter(getChatLibContext);

    isInitialized = true;

    // Subscribe to derived stores and forward to context stores
    // Even though history/_isLoading use context stores, sections/isLoading are derived
    chatServiceInstance?.sections.subscribe((sections) => {
      chatServiceSections?.set(sections);
    });

    chatServiceInstance?.isLoading.subscribe((isLoading) => {
      chatServiceIsLoading?.set(isLoading);
    });

    return chatServiceInstance;
  } catch (error) {
    console.error('[ChatService] Error in initializeChatService():', error);
    console.error('[ChatService] Stack:', error instanceof Error ? error.stack : 'No stack');
    throw error;
  }
}

// Export the singleton directly - stores are always available with default values
export const getChatService = async (): Promise<ChatService> => {
  const handler = await getHandlerAsync();
  if (!handler?.chatService) {
    throw new Error('Handler does not have a chat service');
  }
  return handler.chatService;
};
